import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Box, Stack, Typography, Button, Alert, CircularProgress, IconButton, Skeleton } from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import { useMonthlyReports } from "@/features/profit-calculators/calculator-shared/hooks/useMonthlyReports"
import { getMonthlyReportDetail } from "@/features/profit-calculators/calculator-shared/services/monthlyReportsService"
import type {
  MeeshoCombinedReport,
  PersistedMonthlyReport
} from "@/features/profit-calculators/calculator-shared/types"
import ReportView from "@/features/profit-calculators/calculator-shared/components/ReportView"

interface Props {
  platform?: "meesho" | "amazon"
}

const buildReportForView = (
  summary: PersistedMonthlyReport,
  detail: { orders: MeeshoCombinedReport["orders"]; files: MeeshoCombinedReport["files"]; amazonMetadata?: unknown }
): MeeshoCombinedReport => {
  const report: MeeshoCombinedReport = {
    periodLabel: summary.periodLabel,
    reportId: summary.reportId,
    totalOrders: summary.totalOrders,
    totalUnits: summary.totalUnits,
    totalSettlement: summary.totalSettlement,
    totalPurchaseCost: summary.totalPurchaseCost,
    totalAdsCost: summary.totalAdsCost,
    totalReferral: summary.totalReferral,
    netProfit: summary.netProfit,
    deliveredSplit: summary.deliveredSplit,
    undeliveredSplit: summary.undeliveredSplit,
    skuReports: summary.skuReports,
    orders: detail.orders,
    files: detail.files,
    ...(detail.amazonMetadata ? { amazonMetadata: detail.amazonMetadata } : {})
  } as MeeshoCombinedReport
  return report
}

const ReportPage: React.FC<Props> = ({ platform = "meesho" }) => {
  const { monthKey = "" } = useParams<{ monthKey: string }>()
  const navigate = useNavigate()
  const reportYear = Number(monthKey.slice(0, 4)) || new Date().getFullYear()
  const { getReport } = useMonthlyReports(platform, reportYear)

  const [summary, setSummary] = useState<PersistedMonthlyReport | null | undefined>(undefined)
  const [report, setReport] = useState<MeeshoCombinedReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useMemo(
    () => async () => {
      setLoading(true)
      setError(null)
      try {
        const found = await getReport(monthKey)
        setSummary(found)
        if (found) {
          const detail = await getMonthlyReportDetail(found.detailStorageRef)
          setReport(buildReportForView(found, detail))
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load this report. Please try again.")
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthKey]
  )

  useEffect(() => {
    load()
  }, [load])

  return (
    <Box sx={{ pb: 6 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <IconButton onClick={() => navigate(`/${platform}-calculator`)} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          Back to Monthly Reports
        </Typography>
      </Stack>

      {loading && (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={48} width="40%" />
          <Skeleton variant="rounded" height={220} />
          <Skeleton variant="rounded" height={320} />
        </Stack>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
          sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && summary === null && (
        <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 10 }}>
          <Typography variant="h6" fontWeight={700}>
            No report found for this month
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This report may have been deleted, or hasn't been generated yet.
          </Typography>
          <Button variant="contained" onClick={() => navigate(`/${platform}-calculator`)} sx={{ borderRadius: 2 }}>
            Back to Monthly Reports
          </Button>
        </Stack>
      )}

      {!loading && !error && report && <ReportView platform={platform} report={report} readOnly />}

      {!loading && !error && summary && !report && (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
          <CircularProgress />
        </Stack>
      )}
    </Box>
  )
}

export default ReportPage
